import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AppState, AppAction, Screen, AssessmentData, BodyTypeResult } from '../types';
import { saveUserEmail, saveAssessmentResult } from '../utils/storage';
import { generateId } from '../utils';

const initialState: AppState = {
  currentScreen: 'welcome',
  isLoading: false,
  error: undefined
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_SCREEN':
      return {
        ...state,
        currentScreen: action.payload,
        error: undefined
      };
    
    case 'SET_ASSESSMENT_DATA':
      return {
        ...state,
        assessmentData: action.payload
      };
    
    case 'SET_RESULT':
      return {
        ...state,
        currentResult: action.payload,
        currentScreen: 'email-registration'
      };
    
    case 'SET_USER_EMAIL':
      // Save email and persist current result to history (if available)
      if (action.payload) {
        saveUserEmail(action.payload);
        if (state.currentResult) {
          saveAssessmentResult({
            id: generateId(),
            result: state.currentResult,
            timestamp: new Date(),
            notes: `Assessment completed on ${new Date().toLocaleDateString()}`,
            userEmail: action.payload
          });
        }
      }
      return {
        ...state,
        userEmail: action.payload,
        currentScreen: 'results'
      };
    
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };
    
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
    
    case 'RESET_APP':
      return {
        ...initialState
      };
    
    default:
      return state;
  }
}

interface AssessmentContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  navigateToScreen: (screen: Screen) => void;
  setAssessmentData: (data: AssessmentData) => void;
  setResult: (result: BodyTypeResult) => void;
  setUserEmail: (email: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | undefined) => void;
  resetApp: () => void;
}

const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

interface AssessmentProviderProps {
  children: ReactNode;
}

export function AssessmentProvider({ children }: AssessmentProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  
  const navigateToScreen = (screen: Screen) => {
    dispatch({ type: 'SET_SCREEN', payload: screen });
  };
  
  const setAssessmentData = (data: AssessmentData) => {
    dispatch({ type: 'SET_ASSESSMENT_DATA', payload: data });
  };
  
  const setResult = (result: BodyTypeResult) => {
    dispatch({ type: 'SET_RESULT', payload: result });
  };
  
  const setUserEmail = (email: string) => {
    dispatch({ type: 'SET_USER_EMAIL', payload: email });
  };
  
  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };
  
  const setError = (error: string | undefined) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };
  
  const resetApp = () => {
    dispatch({ type: 'RESET_APP' });
  };
  
  const value: AssessmentContextType = {
    state,
    dispatch,
    navigateToScreen,
    setAssessmentData,
    setResult,
    setUserEmail,
    setLoading,
    setError,
    resetApp
  };
  
  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
}

export function useAssessment() {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
    }
  return context;
}